import {
  Button,
  Card,
  IconButton,
  makeStyles,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CardTitle from "@saleor/components/CardTitle";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import {
  SortableTableBody,
  SortableTableRow
} from "@saleor/components/SortableTable";
import { ProductLensTypeList_productLensTypes_edges_node } from "@saleor/productLensType/types/ProductLensTypeList";
import { PrescriptionStartSelectEnum } from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection, stopPropagation } from "../../../misc";
import { ReorderAction } from "../../../types";

export interface ProductLensTypeProps {
  data: ProductLensTypeList_productLensTypes_edges_node[];
  onReorder: ReorderAction;
  onDelete: (id: string) => void;
  onVisible: (id: string, name: string, isActive: boolean) => void;
  onAdd: () => void;
  onUpdate: (
    id: string,
    name: string,
    extraChargeCode: string,
    skuStartCode: string,
    prescriptionStartSelect: PrescriptionStartSelectEnum
  ) => void;
}

const useStyles = makeStyles(
  theme => ({
    columnAdmin: {
      width: "auto",
      textAlign: "center",
      padding: 5,
      wordWrap: "break-word"
    },
    columnData: {
      width: 360,
      textAlign: "center",
      padding: 5,
      wordWrap: "break-word"
    },
    columnPublished: {
      width: 80,
      textAlign: "center"
    },
    columnDrag: {
      width: 48
    },
    columnOrder: {
      width: 80,
      textAlign: "center"
    },

    columnDelete: {
      width: 80,
      textAlign: "center",
      padding: 0
    },
    columnStore: {
      width: "auto",
      textAlign: "center"
    },
    dragIcon: {
      cursor: "grab"
    },
    iconCell: {
      "&:last-child": {
        paddingRight: theme.spacing()
      },
      width: 80
    },
    link: {
      cursor: "pointer"
    }
  }),
  { name: "LensType" }
);

const ProductLensType: React.FC<ProductLensTypeProps> = props => {
  const { data, onReorder, onDelete, onAdd, onUpdate, onVisible } = props;
  const classes = useStyles(props);

  const intl = useIntl();

  return (
    <Card
      style={{ width: "80%", left: "15%", top: "15%", position: "absolute" }}
    >
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Lens Type",
          description: "section header"
        })}
        toolbar={
          <Button color="primary" onClick={onAdd}>
            <FormattedMessage
              defaultMessage="Create Lens Type"
              description="button"
            />
          </Button>
        }
      />
      <ResponsiveTable>
        <TableHead>
          <TableCell className={classes.columnDrag} />
          <TableCell className={classes.columnOrder}>
            <FormattedMessage
              defaultMessage="Order"
              description="Order header"
            />
          </TableCell>
          <TableCell className={classes.columnAdmin}>
            <FormattedMessage defaultMessage="Name" description="Name" />
          </TableCell>
          <TableCell className={classes.columnData}>
            <FormattedMessage
              defaultMessage="Extra Charge Code"
              description="Extra Charge Code"
            />
          </TableCell>
          <TableCell className={classes.columnAdmin}>
            <FormattedMessage
              defaultMessage="Prescription Start Select"
              description="Prescription Start Select"
            />
          </TableCell>
          <TableCell className={classes.columnAdmin}>
            <FormattedMessage
              defaultMessage="SKU Start Code"
              description="SKU Start Code"
            />
          </TableCell>
          <TableCell className={classes.columnDelete}>
            <FormattedMessage defaultMessage="Visible" description="Visible" />
          </TableCell>
          <TableCell className={classes.columnDelete}>
            <FormattedMessage defaultMessage="Delete" description="Delete" />
          </TableCell>
        </TableHead>

        <SortableTableBody onSortEnd={onReorder ? onReorder : null}>
          {renderCollection(
            data,
            (item, index) => (
              <SortableTableRow
                className={!!item ? classes.link : undefined}
                hover={!!item}
                key={maybe(() => item.id)}
                index={index || 0}
                onClick={() => {
                  onUpdate(
                    item.id,
                    item.name,
                    item.extraChargeCode,
                    item.skuStartCode,
                    item.prescriptionStartSelect
                  );
                }}
              >
                <TableCell className={classes.columnOrder}>
                  {maybe(() => item) ? index + 1 : <Skeleton />}
                </TableCell>

                <TableCell className={classes.columnAdmin}>
                  {maybe(() => item.name) ? item.name : null}
                </TableCell>
                <TableCell className={classes.columnData}>
                  {maybe(() => item.extraChargeCode)
                    ? item.extraChargeCode
                    : null}
                </TableCell>
                <TableCell className={classes.columnAdmin}>
                  {maybe(() => item.prescriptionStartSelect)
                    ? item.prescriptionStartSelect
                    : null}
                </TableCell>
                <TableCell className={classes.columnAdmin}>
                  {maybe(() => item.skuStartCode) ? item.skuStartCode : null}
                </TableCell>
                <TableCell className={classes.columnDelete}>
                  {maybe(() => item) ? (
                    <IconButton
                      onClick={stopPropagation(() =>
                        onVisible(item.id, item.name, !item.isActive)
                      )}
                    >
                      {item.isActive ? (
                        <VisibilityIcon color={"primary"} />
                      ) : (
                        <VisibilityOffIcon color={"secondary"} />
                      )}
                    </IconButton>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>

                <TableCell className={classes.columnDelete}>
                  {maybe(() => item) ? (
                    <IconButton
                      onClick={stopPropagation(() => onDelete(item.id))}
                    >
                      <DeleteIcon color="primary" />
                    </IconButton>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </SortableTableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={7}>
                  <FormattedMessage
                    defaultMessage="No Lens Type found"
                    description="No Lens Type found"
                  />
                </TableCell>
              </TableRow>
            )
          )}
        </SortableTableBody>
      </ResponsiveTable>
    </Card>
  );
};
ProductLensType.displayName = "ProductLensType";
export default ProductLensType;
